import React, { useState, useEffect, useCallback } from 'react';
import { 
    PackagePlus, Trash2, Plus, ArrowLeft, 
    Calculator, AlertTriangle, CheckCircle2, X, Search 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import API from '../../services/api';

const ConsoDep = () => {
    const navigate = useNavigate();

    // États du formulaire de consommation directe par département
    const [departementId, setDepartementId] = useState('');
    const [libelle, setLibelle] = useState('');
    const [observation, setObservation] = useState('');

    // Données et listes de référence
    const [elementsCatalogue, setElementsCatalogue] = useState([]); // MP + PSF
    const [departementsList, setDepartementsList] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedItem, setSelectedItem] = useState(null);
    
    // États de saisie double (Quantité <-> Montant)
    const [quantiteSaisie, setQuantiteSaisie] = useState(''); 
    const [montantSaisie, setMontantSaisie] = useState(0); 

    const [lignesConsommation, setLignesConsommation] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [notification, setNotification] = useState({
        isOpen: false,
        type: 'success',
        message: '',
        onConfirm: null
    });

    const COLORS = {
        primary: '#000000', // Remplacement du bordeaux par le noir
        primaryLight: '#f1f5f9',
        anthracite: '#1e293b',
        success: '#059669',
        danger: '#dc2626',
        border: '#e2e8f0'
    };

    const s = {
        container: { display: 'flex', height: '100vh', background: 'white', overflow: 'hidden' },
        mainArea: { flex: 1, display: 'grid', gridTemplateColumns: '1.2fr 1fr', background: '#f8fafc', position: 'relative', overflow: 'hidden' },
        leftCol: { padding: '20px', display: 'flex', flexDirection: 'column', borderRight: `2px solid ${COLORS.border}`, overflow: 'hidden' },
        rightCol: { display: 'flex', flexDirection: 'column', background: '#f1f5f9', overflow: 'hidden' },
        headerPanier: { background: COLORS.primary, color: 'white', padding: '15px 20px', fontWeight: '800', textAlign: 'center', fontSize: '13px', textTransform: 'uppercase', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
        saisieSection: { padding: '15px 20px', display: 'flex', flexDirection: 'column', background: 'white', borderBottom: `2px solid ${COLORS.border}`, gap: '12px' },
        qtyRow: { display: 'flex', gap: '8px', alignItems: 'flex-end', width: '100%' },
        inputQty: { width: '100%', padding: '10px 6px', fontSize: '18px', fontWeight: '900', textAlign: 'center', border: `2px solid ${COLORS.primary}`, borderRadius: '8px', outline: 'none' },
        inputAmount: { width: '100%', padding: '10px 6px', fontSize: '18px', fontWeight: '900', textAlign: 'center', border: `2px solid ${COLORS.success}`, borderRadius: '8px', outline: 'none', background: '#f0fdf4', color: COLORS.success },
        btnAjouter: { padding: '0 12px', height: '52px', background: COLORS.primary, color: 'white', border: 'none', borderRadius: '8px', fontWeight: '900', cursor: 'pointer', fontSize: '11px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
        listeSection: { flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' },
        table: { width: '100%', borderCollapse: 'collapse' },
        thead: { background: COLORS.anthracite, color: 'white', position: 'sticky', top: 0, zIndex: 10 },
        footer: { padding: '15px 20px', background: 'white', borderTop: `2px solid ${COLORS.border}`, display: 'flex', gap: '10px' },
        selectedRow: { background: COLORS.primaryLight, borderLeft: `4px solid ${COLORS.primary}` },
        toastContainer: {
            position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)',
            zIndex: 1000, display: 'flex', alignItems: 'center',
            background: 'white', padding: '12px 20px', borderRadius: '15px',
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            border: `1px solid ${COLORS.border}`,
            minWidth: '400px', transition: 'all 0.3s ease'
        },
        confirmBtn: { background: COLORS.primary, color: 'white', border: 'none', padding: '6px 15px', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '11px' },
        cancelBtn: { background: '#f1f5f9', color: '#64748b', border: 'none', padding: '6px 15px', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '11px' }
    };

    useEffect(() => {
        if (notification.isOpen && notification.type !== 'confirm') {
            const timer = setTimeout(() => setNotification(prev => ({ ...prev, isOpen: false })), 4000);
            return () => clearTimeout(timer);
        }
    }, [notification.isOpen, notification.type]);

    const showToast = (message, type = 'success') => {
        setNotification({ isOpen: true, type, message, onConfirm: null });
    };

    const showConfirm = (message, onConfirm) => {
        setNotification({ isOpen: true, type: 'confirm', message, onConfirm });
    };

    // Chargement sécurisé des MP, PSF et Subdivisions Analytiques
    const fetchData = useCallback(async () => {
        try {
            const [resMatieres, resPsf, resDeps] = await Promise.all([
                API.get('/sales/sur-mesure/raw-materials').catch(err => {
                    console.error('[ConsoDep] Erreur matières premières :', err);
                    return { data: [] };
                }),
                API.get('/semi-finished-products').catch(err => {
                    console.error('[ConsoDep] Erreur produits semi-finis :', err);
                    return { data: [] };
                }),
                API.get('/analytique/plan/liste').catch(err => {
                    console.error('[ConsoDep] Erreur chargement subdivisions :', err.response?.status, err.message);
                    return null;
                })
            ]);

            // Traitement Matières Premières
            const rawMatieres = resMatieres.data?.data ?? resMatieres.data ?? [];
            const listMatieres = Array.isArray(rawMatieres) ? rawMatieres.filter(m => Number(m.is_active) === 1).map(m => ({
                id: m.id,
                nom: m.nom,
                type_article: 'MP',
                unite: m.unite_mesure || 'U',
                stock: Number(m.stock_actuel) || 0,
                cmp: Number(m.cmp) || 0
            })) : [];

            // Traitement Produits Semi-Finis
            const rawPsf = resPsf.data?.data ?? resPsf.data ?? [];
            const listPsf = Array.isArray(rawPsf) ? rawPsf.map(p => ({
                id: p.id || p.ID,
                nom: p.nom || p.NOM,
                type_article: 'PSF',
                unite: p.unite_mesure || p.unite || 'U',
                stock: Number(p.stock_actuel) || 0,
                cmp: Number(p.cmp) || 0
            })) : [];

            setElementsCatalogue([...listMatieres, ...listPsf]);

            // Traitement des Subdivisions Analytiques
            if (!resDeps) {
                setDepartementsList([]);
                showToast("Impossible de charger les subdivisions.", "error");
                return;
            }

            const subdivisions = resDeps.data?.data ?? resDeps.data;

            if (!Array.isArray(subdivisions)) {
                console.error('[ConsoDep] Format de réponse inattendu :', subdivisions);
                setDepartementsList([]);
                return;
            }

            setDepartementsList(subdivisions);

        } catch (err) {
            console.error('[ConsoDep] Erreur générale :', err);
            showToast("Erreur lors du chargement des données", "error");
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const coutTotalConso = lignesConsommation.reduce((acc, l) => acc + (l.quantite * l.cmp), 0);

    const handleQtyChange = (val) => {
        setQuantiteSaisie(val);
        const qte = parseFloat(val || 0);
        if (selectedItem) {
            setMontantSaisie(Math.round(qte * selectedItem.cmp));
        }
    };

    const handleAmountChange = (val) => {
        setMontantSaisie(val);
        const montant = parseFloat(val || 0);
        if (selectedItem && selectedItem.cmp > 0) {
            const calculQte = montant / selectedItem.cmp;
            setQuantiteSaisie(Number(calculQte.toFixed(4)));
        }
    };

    const ajouterAuPanier = () => {
        if (!selectedItem) return showToast("Sélectionnez un article dans la liste", "error");

        const qte = parseFloat(quantiteSaisie);
        if (isNaN(qte) || qte <= 0) return showToast("Quantité consommée invalide", "error");

        if (qte > selectedItem.stock) {
            showToast(`Attention : Stock insuffisant (${selectedItem.stock} dispo)`, "error");
            return;
        }

        const existingIndex = lignesConsommation.findIndex(l => l.article_id === selectedItem.id);
        if (existingIndex > -1) {
            const updated = [...lignesConsommation];
            const nouvelleQte = updated[existingIndex].quantite + qte;
            if (nouvelleQte > selectedItem.stock) {
                return showToast("Quantité totale supérieure au stock disponible", "error");
            }
            updated[existingIndex].quantite = nouvelleQte;
            setLignesConsommation(updated);
        } else {
            setLignesConsommation([...lignesConsommation, {
                id: `CONSO-ITEM-${Date.now()}-${Math.random().toString(36).slice(-4)}`,
                article_id: selectedItem.id,
                nom: selectedItem.nom,
                type_article: selectedItem.type_article,
                unite: selectedItem.unite,
                quantite: qte,
                cmp: selectedItem.cmp,
                stockActuel: selectedItem.stock
            }]);
        }
        showToast(`Ajouté : ${selectedItem.nom}`);
        setSelectedItem(null);
        setQuantiteSaisie('');
        setMontantSaisie(0);
    };

    const executerEnregistrement = async () => {
        setIsSubmitting(true);
        try {
            const payload = {
                departement_id: departementId,
                libelle: libelle.trim(),
                observation: observation.trim(),
                lignes: lignesConsommation.map(l => ({
                    type_article: l.type_article || l.type,
                    article_id: l.article_id || l.id,
                    quantite: Number(l.quantite)
                }))
            };

            const response = await API.post('/consommations-departements', payload);

            if (response.data.success) {
                showToast("Consommation de département enregistrée avec succès !", "success");
                setDepartementId('');
                setLibelle('');
                setObservation('');
                setLignesConsommation([]);
                setSelectedItem(null);
                fetchData();
            }
        } catch (err) {
            console.error('[ConsoDep] Erreur enregistrement :', err.response?.data || err.message);
            const errMsg = err.response?.data?.error || err.response?.data?.message || err.message || "Erreur lors de l'enregistrement";
            showToast(errMsg, "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = () => {
        if (!departementId) return showToast("Veuillez sélectionner la subdivision", "error");
        if (!libelle.trim()) return showToast("Le libellé de la consommation est requis", "error");
        if (lignesConsommation.length === 0) return showToast("Ajoutez au moins un article au panier", "error");

        showConfirm(
            "Confirmer la validation de cette consommation et la déduction immédiate des stocks ?",
            () => executerEnregistrement()
        );
    };

    const NotificationToast = () => {
        if (!notification.isOpen) return null;
        const isConfirm = notification.type === 'confirm';
        const color = notification.type === 'error' ? COLORS.danger : (isConfirm ? '#f59e0b' : COLORS.success);

        return (
            <div style={{ ...s.toastContainer, borderLeft: `8px solid ${color}` }}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '35px', height: '35px', borderRadius: '50%', background: `${color}15`, marginRight: '15px', flexShrink: 0 }}>
                    {notification.type === 'error' ? <X size={20} color={color} /> : isConfirm ? <AlertTriangle size={20} color={color} /> : <CheckCircle2 size={20} color={color} />}
                </div>
                <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: '700', color: COLORS.anthracite, fontSize: '14px' }}>{notification.message}</p>
                    {isConfirm && (
                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                            <button onClick={() => notification.onConfirm()} style={s.confirmBtn}>CONFIRMER</button>
                            <button onClick={() => setNotification({isOpen: false})} style={s.cancelBtn}>ANNULER</button>
                        </div>
                    )}
                </div>
                {!isConfirm && (
                    <button onClick={() => setNotification({isOpen: false})} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1' }}><X size={20} /></button>
                )}
            </div>
        );
    };

    return (
        <div style={s.container}>
            <Sidebar />
            <main style={s.mainArea}>
                <NotificationToast />

                {/* COLONNE GAUCHE : Catalogue & Paramètres */}
                <section style={s.leftCol}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                        <button onClick={() => navigate(-1)} style={{ background: '#f1f5f9', border: 'none', padding: '8px', borderRadius: '50%', cursor: 'pointer' }}><ArrowLeft size={18}/></button>
                        <div>
                            <h1 style={{ fontSize: '16px', fontWeight: '900', margin: 0, color: COLORS.anthracite }}>CONSOMMATION DIRECTE PAR DÉPARTEMENT</h1>
                            <p style={{ fontSize: '11px', color: '#64748b', fontWeight: '700' }}>ÉCONOMAT - SORTIES SANS FICHE TECHNIQUE</p>
                        </div>
                    </div>

                    <div style={{ background: 'white', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '15px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: '10px', fontWeight: '900', color: COLORS.primary }}>SUBDIVISION / DÉPARTEMENT *</label>
                                <select 
                                    style={{ width: '100%', padding: '10px', marginTop: '4px', borderRadius: '6px', border: `2px solid ${COLORS.primary}`, fontWeight: '800', background: 'white' }}
                                    value={departementId}
                                    onChange={(e) => setDepartementId(e.target.value)}
                                >
                                    <option value="">-- Choisir une subdivision --</option>
                                    {departementsList.map(d => (
                                        <option key={d.id} value={d.id}>
                                            {d.code ? `${d.code} - ` : ''}
                                            {d.libelle || d.nom || d.NOM}
                                            {d.parent_dept_nom ? ` (${d.parent_dept_nom})` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div style={{ flex: 1.5 }}>
                                <label style={{ fontSize: '10px', fontWeight: '900', color: COLORS.anthracite }}>LIBELLÉ / MOTIF (ex: Recouche suite 01) *</label>
                                <input 
                                    style={{ width: '100%', padding: '10px', marginTop: '4px', borderRadius: '6px', border: '1px solid #cbd5e1', fontWeight: '800' }} 
                                    placeholder="Ex: Utilisation ménage / entretien..." 
                                    value={libelle}
                                    onChange={(e) => setLibelle(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <div style={{ position: 'relative', marginBottom: '10px' }}>
                        <Search style={{ position: 'absolute', right: '12px', top: '10px', color: '#94a3b8' }} size={18} />
                        <input 
                            style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontWeight: '600' }} 
                            placeholder="Rechercher une matière première ou un produit semi-fini..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', background: 'white' }}>
                        <table style={s.table}>
                            <thead style={s.thead}>
                                <tr>
                                    <th style={{ padding: '10px', textAlign: 'left', fontSize: '11px' }}>ARTICLE</th>
                                    <th style={{ padding: '10px', textAlign: 'center', fontSize: '11px' }}>TYPE</th>
                                    <th style={{ padding: '10px', textAlign: 'center', fontSize: '11px' }}>STOCK ACTUEL</th>
                                    <th style={{ padding: '10px', textAlign: 'right', fontSize: '11px' }}>CMP</th>
                                    <th style={{ padding: '10px', textAlign: 'center', fontSize: '11px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {elementsCatalogue
                                    .filter(item => item.nom.toLowerCase().includes(searchTerm.toLowerCase()))
                                    .map(item => (
                                        <tr 
                                            key={item.id} 
                                            onClick={() => {
                                                setSelectedItem(item);
                                                setQuantiteSaisie('');
                                            }} 
                                            style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer', ...(selectedItem?.id === item.id ? s.selectedRow : {}) }}
                                        >
                                            <td style={{ padding: '10px' }}>
                                                <div style={{ fontWeight: '800', fontSize: '12px', color: COLORS.anthracite }}>{item.nom}</div>
                                                <div style={{ fontSize: '10px', color: '#64748b' }}>Unité : {item.unite}</div>
                                            </td>
                                            <td style={{ padding: '10px', textAlign: 'center' }}>
                                                <span style={{ 
                                                    padding: '3px 8px', borderRadius: '4px', fontSize: '9px', fontWeight: '900',
                                                    background: item.type_article === 'MP' ? '#e0f2fe' : '#fef3c7',
                                                    color: item.type_article === 'MP' ? '#0369a1' : '#d97706'
                                                }}>
                                                    {item.type_article === 'MP' ? 'MATIÈRE PREMIÈRE' : 'PRODUIT SEMI-FINI'}
                                                </span>
                                            </td>
                                            <td style={{ padding: '10px', textAlign: 'center', fontWeight: '700', color: item.stock <= 0 ? COLORS.danger : COLORS.success, fontSize: '12px' }}>
                                                {item.stock} {item.unite}
                                            </td>
                                            <td style={{ padding: '10px', textAlign: 'right', fontWeight: '900', color: COLORS.anthracite, fontSize: '13px' }}>
                                                {Math.round(item.cmp).toLocaleString()} FCFA
                                            </td>
                                            <td style={{ padding: '10px', textAlign: 'center' }}>
                                                <PackagePlus size={16} color={selectedItem?.id === item.id ? COLORS.primary : '#cbd5e1'}/>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* COLONNE DROITE : Panier & Validation */}
                <section style={s.rightCol}>
                    <div style={s.headerPanier}>
                        <span>Articles Consommés</span>
                        <span style={{ color: '#fbbf24', fontSize: '14px' }}>{Math.round(coutTotalConso).toLocaleString()} FCFA</span>
                    </div>

                    <div style={s.saisieSection}>
                        <div style={{ padding: '10px 12px', background: selectedItem ? COLORS.primaryLight : '#f1f5f9', borderRadius: '6px', border: selectedItem ? `2.5px solid ${COLORS.primary}` : '2.5px dashed #cbd5e1', margin: '0 5px' }}>
                            <span style={{ fontSize: '9px', fontWeight: '900', color: COLORS.primary }}>ARTICLE SÉLECTIONNÉ :</span>
                            <div style={{ fontSize: '13px', fontWeight: '900', color: COLORS.anthracite, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedItem ? `${selectedItem.nom} (${selectedItem.type_article})` : "AUCUN ARTICLE SÉLECTIONNÉ"}</span>
                                {selectedItem && <span style={{ fontSize: '10px', color: '#64748b', flexShrink: 0, marginLeft: '8px' }}>Stock dispo: {selectedItem.stock}</span>}
                            </div>
                        </div>

                        <div style={s.qtyRow}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <label style={{ fontSize: '10px', fontWeight: '900', color: '#64748b' }}>QTÉ ({selectedItem?.unite || '---'}) *</label>
                                <input 
                                    type="number" 
                                    style={s.inputQty} 
                                    value={quantiteSaisie} 
                                    onChange={(e) => handleQtyChange(e.target.value)} 
                                    min="0.0001" 
                                    step="0.0001" 
                                    placeholder="0"
                                />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <label style={{ fontSize: '10px', fontWeight: '900', color: COLORS.success }}>VALEUR (FCFA)</label>
                                <input 
                                    type="number" 
                                    style={s.inputAmount} 
                                    value={montantSaisie} 
                                    onChange={(e) => handleAmountChange(e.target.value)} 
                                    min="0"
                                    step="1"
                                />
                            </div>
                            <button 
                                onClick={ajouterAuPanier} 
                                style={{ ...s.btnAjouter, opacity: selectedItem ? 1 : 0.5, cursor: selectedItem ? 'pointer' : 'not-allowed' }}
                                disabled={!selectedItem}
                            >
                                <Plus size={16}/><span>AJOUTER</span>
                            </button>
                        </div>
                    </div>

                    <div style={s.listeSection}>
                        <table style={s.table}>
                            <thead style={{ background: COLORS.primary, color: 'white' }}>
                                <tr>
                                    <th style={{ padding: '10px', textAlign: 'left', fontSize: '11px' }}>Article Consommé</th>
                                    <th style={{ padding: '10px', textAlign: 'center', fontSize: '11px' }}>Quantité</th>
                                    <th style={{ padding: '10px', textAlign: 'right', fontSize: '11px' }}>Valeur Totale</th>
                                    <th style={{ padding: '10px', textAlign: 'right', fontSize: '11px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {lignesConsommation.map((ligne) => (
                                    <tr key={ligne.id} style={{ borderBottom: '1px solid #cbd5e1', background: 'white' }}>
                                        <td style={{ padding: '10px' }}>
                                            <div style={{ fontWeight: '800', fontSize: '12px' }}>{ligne.nom}</div>
                                            <div style={{ fontSize: '10px', color: '#64748b' }}>CMP : {Math.round(ligne.cmp).toLocaleString()} / {ligne.unite}</div>
                                        </td>
                                        <td style={{ padding: '10px', textAlign: 'center', fontWeight: '900', fontSize: '12px', color: COLORS.anthracite }}>
                                            {ligne.quantite} {ligne.unite}
                                        </td>
                                        <td style={{ padding: '10px', textAlign: 'right', fontWeight: '900', color: COLORS.success, fontSize: '13px' }}>
                                            {Math.round(ligne.quantite * ligne.cmp).toLocaleString()} FCFA
                                        </td>
                                        <td style={{ padding: '10px', textAlign: 'right' }}>
                                            <button 
                                                onClick={() => setLignesConsommation(lignesConsommation.filter(l => l.id !== ligne.id))} 
                                                title="Supprimer"
                                                style={{ color: '#ef4444', border: 'none', background: '#fee2e2', padding: '6px', borderRadius: '5px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {lignesConsommation.length === 0 && (
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', padding: '30px' }}>
                                <Calculator size={40} strokeWidth={1} />
                                <p style={{ fontWeight: '800', fontSize: '11px', marginTop: '8px' }}>AUCUN ARTICLE AJOUTÉ AU PANIER</p>
                            </div>
                        )}
                    </div>

                    <div style={{ padding: '10px 20px', background: 'white', borderTop: '1px solid #e2e8f0' }}>
                        <input 
                            style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '11px' }}
                            placeholder="Observations complémentaires..."
                            value={observation}
                            onChange={(e) => setObservation(e.target.value)}
                        />
                    </div>

                    <div style={s.footer}>
                        <button 
                            style={{ background: '#f1f5f9', color: '#475569', border: 'none', padding: '12px 20px', borderRadius: '8px', fontWeight: '800', cursor: 'pointer', fontSize: '12px' }} 
                            onClick={() => { setLignesConsommation([]); setLibelle(''); setObservation(''); setDepartementId(''); setSelectedItem(null); }}
                        >
                            RÉINITIALISER
                        </button>
                        <button 
                            onClick={handleSubmit} 
                            disabled={isSubmitting || lignesConsommation.length === 0}
                            style={{ 
                                flex: 1, background: COLORS.success, color: 'white', border: 'none', padding: '12px', borderRadius: '8px', 
                                fontWeight: '900', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', 
                                opacity: (isSubmitting || lignesConsommation.length === 0) ? 0.6 : 1, fontSize: '13px' 
                            }}
                        >
                            <PackagePlus size={18} /> {isSubmitting ? "ENREGISTREMENT..." : "VALIDER LA CONSOMMATION"}
                        </button>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default ConsoDep;